/**
 * Saved Site contract — shared wire shape for the user-defined "saved sites"
 * library surfaced in web at `/saved-data` and on mobile via Settings →
 * Library → Saved Sites.
 *
 * Web endpoints live at `/api/saved-sites` and `/api/saved-sites/[id]`. The
 * mobile mirror at `/api/mobile/v1/account/saved-sites` consumes these
 * schemas via `safeParseOrWarn`.
 *
 * Prisma model `SavedSite` columns:
 *   id, userId, siteName, addressLine1, addressLine2?, city?, county?,
 *   postcode, nearestHospital?, nearestHospitalAddress?, siteNotes?
 *
 * The wire schema exposes every user-visible column. `userId`, `createdAt`,
 * `updatedAt` stay server-side. Optional columns are `null` on the wire (not
 * `undefined`) so React Query caches stay structurally stable.
 */
import { z } from "zod";

// UK postcode shape — matches the regex used by the web POST/PUT route.
const UK_POSTCODE_REGEX =
  /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export const SavedSiteSchema = z.object({
  id: z.string(),
  siteName: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  postcode: z.string(),
  nearestHospital: z.string().nullable(),
  nearestHospitalAddress: z.string().nullable(),
  // Added after the v1 mobile contract shipped, so it stays optional for
  // backwards-compatible parsing. New servers always send it. Absence must
  // fail toward "do not prefill" at Project pickers.
  nearestHospitalConfirmed: z.boolean().optional(),
  siteNotes: z.string().nullable(),
});
export type SavedSite = z.infer<typeof SavedSiteSchema>;

export const SavedSiteCreateSchema = z.object({
  siteName: z.string().min(1).max(200),
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).optional(),
  city: z.string().max(120).optional(),
  county: z.string().max(120).optional(),
  postcode: z
    .string()
    .min(1)
    .max(12)
    .regex(UK_POSTCODE_REGEX, "Invalid UK postcode"),
  nearestHospital: z.string().max(200).optional(),
  nearestHospitalAddress: z.string().max(400).optional(),
  siteNotes: z.string().max(2000).optional(),
});
export type SavedSiteCreate = z.infer<typeof SavedSiteCreateSchema>;

export const SavedSiteUpdateSchema = z.object({
  siteName: z.string().min(1).max(200).optional(),
  addressLine1: z.string().min(1).max(200).optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  county: z.string().max(120).nullable().optional(),
  postcode: z
    .string()
    .min(1)
    .max(12)
    .regex(UK_POSTCODE_REGEX, "Invalid UK postcode")
    .optional(),
  nearestHospital: z.string().max(200).nullable().optional(),
  nearestHospitalAddress: z.string().max(400).nullable().optional(),
  siteNotes: z.string().max(2000).nullable().optional(),
});
export type SavedSiteUpdate = z.infer<typeof SavedSiteUpdateSchema>;
